import { getHighestNumber } from '@/util/number';
import StyledTreeItem from '@/components/treemenu/StyledTreeItem';
import TreeLabel from '@/components/treemenu/TreeLabel';
import { SelectedItem } from '@/components/treemenu/TreeMenu';
import { useDialogs } from '@/hooks/useDialogs';
import { Application } from '@/types/application';
import { SubroutineFolder } from '@/types/subroutine';
import { AccountTree } from '@mui/icons-material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { FC, useEffect, useState } from 'react';

type HandleFoldersDialogProps = {
  open: boolean;
  selectedItem?: SelectedItem;
  folders?: SubroutineFolder[];
  application: Application;
  onClose: () => void;
  setApplication: (newApplication: Application) => void;
};

const HandleFoldersDialog: FC<HandleFoldersDialogProps> = (props) => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<SubroutineFolder | undefined>();
  const [newName, setNewName] = useState('');

  const { confirm } = useDialogs();

  const renderFolder = (folder: SubroutineFolder) => {
    return (
      <StyledTreeItem
        key={`folder.${folder.id}`}
        itemId={`${folder.id}`}
        label={folder.label}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedFolder(folder);
        }}
      >
        {props.folders?.map((innerFolder) => innerFolder.parentId === folder.id && renderFolder(innerFolder))}
      </StyledTreeItem>
    );
  };

  const flattenFolders = (folders: SubroutineFolder[]) => {
    const result: string[] = ['folder'];
    folders.forEach((folder) => result.push(folder.id.toString()));
    return result;
  };

  const handleClose = () => {
    setSelectedFolder(undefined);
    setNewName('');
    props.onClose();
  };

  const addNewFolder = (label: string, id?: number) => {
    const folders = props.application.folders || [];
    const newFolder = {
      id: getHighestNumber(folders, 'id') + 1,
      parentId: id || 0,
      label: label
    };

    props.setApplication({ ...props.application, folders: [...folders, newFolder] });
  };

  const moveItemToFolder = (newFolderId: number | undefined) => {
    if (props.selectedItem?.type === 'subroutine') {
      props.setApplication({
        ...props.application,
        subroutines: props.application.subroutines.map((subroutine) =>
          subroutine.id === props.selectedItem?.id
            ? {
                ...subroutine,
                folder: newFolderId
              }
            : subroutine
        )
      });
    } else if (props.selectedItem?.type === 'folder') {
      props.setApplication({
        ...props.application,
        folders: props.application.folders?.map((folder) =>
          folder.id === props.selectedItem?.id ? { ...folder, parentId: newFolderId || 0 } : folder
        )
      });
    }
  };

  useEffect(() => props.folders && setExpanded(flattenFolders(props.folders)), [props.folders]);
  useEffect(
    () => setSelectedFolder(props.application?.folders?.find((folder) => folder.id === props.selectedItem?.folderId)),
    [props.selectedItem]
  );

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{props.selectedItem?.id ? 'Move to Folder' : 'Add Folder'}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography variant='h6' color='text.secondary'>
          Placing {props.selectedItem?.id ? props.selectedItem.name : 'new folder'} under:{' '}
          {selectedFolder?.label || 'Flow'}
        </Typography>
        <SimpleTreeView
          selectedItems={selectedFolder?.id.toString() || 'folder'}
          slots={{ collapseIcon: ExpandMore, expandIcon: ChevronRight }}
          sx={{
            width: '100%',
            maxHeight: '20rem',
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'text.secondary',
            borderRadius: '0.25rem',
            padding: 1
          }}
          expandedItems={expanded}
        >
          <StyledTreeItem
            itemId='folder'
            label={<TreeLabel icon={AccountTree} label='Flow' />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedFolder(undefined);
            }}
          >
            {props.folders?.map((folder) => !folder.parentId && renderFolder(folder))}
          </StyledTreeItem>
        </SimpleTreeView>
        {!props.selectedItem?.id && (
          <TextField
            label={'Folder Name'}
            size='small'
            autoFocus={props.open}
            value={newName}
            onChange={(event) => setNewName(event.currentTarget.value)}
            sx={{ mt: 1 }}
            InputProps={{ autoComplete: 'off' }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (!props.selectedItem?.id && !newName) {
              confirm({
                title: `Empty Folder Name`,
                description: `Folder Name cannot be empty`,
                confirmationText: 'OK',
                hideCancelButton: true
              });
              return;
            }
            props.selectedItem?.id ? moveItemToFolder(selectedFolder?.id) : addNewFolder(newName, selectedFolder?.id);
            handleClose();
          }}
          color='primary'
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HandleFoldersDialog;
